import {
  getSessionItem,
  removeSessionItem,
  SessionStorageKeys,
  setSessionItem
} from '@nankai/admin/utils/sessionStorage';
import { onIdTokenChanged } from 'firebase/auth';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from 'react';
import { UserGroup, UserRole } from '../constants';
import { firebaseAuth } from '../vendors/firebase';

export type User = {
  uid: string;
  email: string;
  displayName: string;
  userRole: UserRole;
  userGroup: UserGroup;
};

type AuthContextType = {
  user: User | null;
  idToken: string | null;
  setUser: (user: User | null) => void;
  setIdToken: (idToken: string | null) => void;
  logout: () => void;
  isInitializing: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    // 起動時にセッション復元
    const storedUser = getSessionItem<User>(SessionStorageKeys.USER);
    const storedIdToken = getSessionItem<string>(SessionStorageKeys.ID_TOKEN);

    if (storedUser) setUser(storedUser);
    if (storedIdToken) setIdToken(storedIdToken);

    setIsInitializing(false);
  }, []);

  // トークンとユーザーの永続化
  useEffect(() => {
    if (user) {
      setSessionItem(SessionStorageKeys.USER, user);
    } else {
      removeSessionItem(SessionStorageKeys.USER);
    }
  }, [user]);

  // FIrebase idToken自動更新
  useEffect(() => {
    // Firebase側でIDトークンが更新されたら自動でsetIdToken
    const unsubscribe = onIdTokenChanged(firebaseAuth, async user => {
      if (user) {
        const freshToken = await user.getIdToken(true);
        setIdToken(freshToken);
      } else {
        // ログアウト状態
        setIdToken(null);
        setUser(null);
      }
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (idToken) {
      setSessionItem(SessionStorageKeys.ID_TOKEN, idToken);
    } else {
      removeSessionItem(SessionStorageKeys.ID_TOKEN);
    }
  }, [idToken]);

  const logout = () => {
    removeSessionItem(SessionStorageKeys.USER);
    removeSessionItem(SessionStorageKeys.ID_TOKEN);
    setUser(null);
    setIdToken(null);
  };

  return (
    <AuthContext.Provider
      value={{ user, idToken, setUser, setIdToken, logout, isInitializing }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
